// ============================================================
// HUD — in-game heads-up display
// ============================================================
import * as PIXI from 'pixi.js';
import { GAME_WIDTH, COLOR, Z } from '../constants.js';

export class HUD {
  constructor(stage) {
    this.stage = stage;
    this.container = new PIXI.Container();
    this.container.zIndex = Z.ui;
    stage.addChild(this.container);

    this._score   = 0;
    this._lives   = 3;
    this._timer   = null;
    this._time    = 0;
    this._hints   = [];
    this._msgTimer = 0;
    this._buildUI();
  }

  _buildUI() {
    const c = this.container;

    // ── Top bar background ──
    const topBar = new PIXI.Graphics();
    topBar.rect(0, 0, GAME_WIDTH, 54).fill({ color: COLOR.hud, alpha: 0.88 });
    topBar.rect(0, 52, GAME_WIDTH, 2).fill({ color: 0x1DE9B6, alpha: 0.8 });
    c.addChild(topBar);

    // ── Lives ──
    this._livesText = new PIXI.Text({
      text: '🐢🐢🐢',
      style: { fontSize: 28 },
    });
    this._livesText.x = 16;
    this._livesText.y = 10;
    c.addChild(this._livesText);

    // ── Score ──
    this._scoreLbl = new PIXI.Text({
      text: 'SCORE',
      style: { fontSize: 11, fill: 0x80CBC4, fontWeight: 'bold', letterSpacing: 2 },
    });
    this._scoreLbl.anchor.set(0.5, 0);
    this._scoreLbl.x = GAME_WIDTH / 2;
    this._scoreLbl.y = 4;
    c.addChild(this._scoreLbl);

    this._scoreText = new PIXI.Text({
      text: '0',
      style: { fontSize: 26, fill: 0xFFD700, fontWeight: 'bold' },
    });
    this._scoreText.anchor.set(0.5, 0);
    this._scoreText.x = GAME_WIDTH / 2;
    this._scoreText.y = 18;
    c.addChild(this._scoreText);

    // ── Timer (optional) ──
    this._timerText = new PIXI.Text({
      text: '',
      style: { fontSize: 22, fill: 0xFFB74D, fontWeight: 'bold' },
    });
    this._timerText.anchor.set(1, 0);
    this._timerText.x = GAME_WIDTH - 16;
    this._timerText.y = 12;
    c.addChild(this._timerText);

    // ── Level name ──
    this._levelText = new PIXI.Text({
      text: '',
      style: { fontSize: 14, fill: 0xB0C4DE },
    });
    this._levelText.anchor.set(1, 1);
    this._levelText.x = GAME_WIDTH - 16;
    this._levelText.y = 50;
    c.addChild(this._levelText);

    // ── Hint pills (bottom bar) ──
    this._hintBar = new PIXI.Container();
    this._hintBar.y = 680;
    c.addChild(this._hintBar);

    // ── Message banner ──
    this._msgBanner = new PIXI.Container();
    this._msgBanner.visible = false;
    const msgBg = new PIXI.Graphics();
    msgBg.roundRect(-240, -24, 480, 48, 12).fill({ color: 0x1A237E, alpha: 0.92 });
    msgBg.roundRect(-240, -24, 480, 48, 12).stroke({ color: 0x7C4DFF, width: 2 });
    this._msgText = new PIXI.Text({
      text: '',
      style: { fontSize: 22, fill: 0xFFFFFF, fontWeight: 'bold', align: 'center' },
    });
    this._msgText.anchor.set(0.5);
    this._msgBanner.addChild(msgBg);
    this._msgBanner.addChild(this._msgText);
    this._msgBanner.x = GAME_WIDTH / 2;
    this._msgBanner.y = 110;
    this._msgBanner.zIndex = Z.ui + 5;
    c.addChild(this._msgBanner);

    // ── Spawn message (bottom-center) ──
    this._spawnMsg = new PIXI.Text({
      text: '',
      style: { fontSize: 18, fill: 0x1DE9B6, fontWeight: 'bold',
               stroke: { color: 0x000000, width: 4 },
               dropShadow: { color: 0x000000, blur: 4, distance: 2 } },
    });
    this._spawnMsg.anchor.set(0.5, 1);
    this._spawnMsg.x = GAME_WIDTH / 2;
    this._spawnMsg.y = 670;
    this._spawnMsg.zIndex = Z.ui;
    c.addChild(this._spawnMsg);
    this._spawnMsgTimer = 0;

    // ── Trash can (bottom-right, drag objects here to delete) ──
    this._buildTrashCan();
  }

  _buildTrashCan() {
    const SIZE = 54;
    const MARGIN = 12;
    const cx = GAME_WIDTH - MARGIN - SIZE / 2;
    const cy = 720 - MARGIN - SIZE / 2 - 38;   // just above hint bar

    this._trashCan = new PIXI.Container();
    this._trashCan.x = cx;
    this._trashCan.y = cy;
    this._trashCan.zIndex = Z.ui + 2;
    this._trashCan.visible = false;   // only shown while dragging
    this.container.addChild(this._trashCan);

    // Bin body
    const bin = new PIXI.Graphics();
    // Lid
    bin.rect(-SIZE/2 - 4, -SIZE/2 - 8, SIZE + 8, 10).fill({ color: 0x546E7A });
    // Body
    bin.rect(-SIZE/2, -SIZE/2 + 4, SIZE, SIZE - 4).fill({ color: 0x607D8B });
    // Stripes inside
    for (let i = 0; i < 3; i++) {
      bin.rect(-SIZE/2 + 8 + i*14, -SIZE/2 + 12, 6, SIZE - 20).fill({ color: 0x455A64, alpha: 0.6 });
    }
    // Rim highlight
    bin.rect(-SIZE/2, -SIZE/2 + 2, SIZE, 4).fill({ color: 0x78909C });
    this._trashCan.addChild(bin);
    this._trashBinGfx = bin;

    // Label
    const lbl = new PIXI.Text({
      text: '🗑️',
      style: { fontSize: 32 },
    });
    lbl.anchor.set(0.5, 0.5);
    this._trashCan.addChild(lbl);

    // Hover text
    const hint = new PIXI.Text({
      text: 'drop to delete',
      style: { fontSize: 11, fill: 0xB0BEC5, align: 'center' },
    });
    hint.anchor.set(0.5, 0);
    hint.y = SIZE / 2 + 4;
    this._trashCan.addChild(hint);

    // Store game-space rect for hit-testing
    this._trashRect = {
      x: cx - SIZE / 2 - 10,
      y: cy - SIZE / 2 - 14,
      w: SIZE + 20,
      h: SIZE + 24,
    };
  }

  /** Show/hide the trash can while dragging */
  showTrash(show) {
    if (this._trashCan) this._trashCan.visible = show;
  }

  /** Highlight the trash can when hovered */
  highlightTrash(on) {
    if (!this._trashCan) return;
    this._trashCan.scale.set(on ? 1.15 : 1.0);
    this._trashBinGfx.tint = on ? 0xFF5252 : 0xFFFFFF;
  }

  /** Returns true if screen-space (sx, sy) is over the trash can */
  isOverTrash(sx, sy) {
    if (!this._trashRect || !this._trashCan?.visible) return false;
    const r = this._trashRect;
    return sx >= r.x && sx <= r.x + r.w && sy >= r.y && sy <= r.y + r.h;
  }

  setLevel(levelData) {
    this._levelText.text = `Level ${levelData.id}: ${levelData.name}`;
    this._timer = levelData.timelimit ?? null;
    this._time  = this._timer ?? 0;
    this._setHints(levelData.hints ?? []);
  }

  _setHints(hints) {
    this._hintBar.removeChildren();

    const label = new PIXI.Text({
      text: '💡 Try:',
      style: { fontSize: 14, fill: 0x80CBC4 },
    });
    label.x = 10;
    this._hintBar.addChild(label);

    let x = 70;
    for (const hint of hints.slice(0, 6)) {
      const pill = new PIXI.Container();
      const bg = new PIXI.Graphics();
      const tw = hint.length * 9 + 24;
      bg.roundRect(-tw/2, -13, tw, 26, 13).fill({ color: 0x1A237E, alpha: 0.85 });
      bg.roundRect(-tw/2, -13, tw, 26, 13).stroke({ color: 0x3F51B5, width: 1 });
      const t = new PIXI.Text({ text: hint, style: { fontSize: 13, fill: 0xC5CAE9 } });
      t.anchor.set(0.5);
      pill.addChild(bg);
      pill.addChild(t);
      pill.x = x + tw / 2;
      pill.y = 13;
      x += tw + 8;
      this._hintBar.addChild(pill);
    }
  }

  setScore(score) {
    this._score = score;
    this._scoreText.text = String(score);
    // Animate
    this._scoreText.scale.set(1.4);
    setTimeout(() => this._scoreText.scale.set(1), 200);
  }

  setLives(lives) {
    this._lives = lives;
    this._livesText.text = '🐢'.repeat(Math.max(0, lives));
  }

  showMessage(msg, duration = 2.0) {
    this._msgText.text = msg;
    this._msgBanner.visible = true;
    this._msgBanner.alpha = 1;
    this._msgTimer = duration;
  }

  showSpawnMsg(msg) {
    this._spawnMsg.text = msg;
    this._spawnMsg.alpha = 1;
    this._spawnMsgTimer = 2.5;
  }

  update(dt) {
    // Timer countdown
    if (this._timer !== null && this._time > 0) {
      this._time -= dt;
      if (this._time < 0) this._time = 0;
      const secs = Math.ceil(this._time);
      this._timerText.text = `⏱ ${secs}s`;
      this._timerText.tint = secs < 15 ? 0xFF5722 : 0xFFFFFF;
    }

    // Message fade
    if (this._msgTimer > 0) {
      this._msgTimer -= dt;
      if (this._msgTimer <= 0) {
        this._msgBanner.visible = false;
      } else if (this._msgTimer < 0.5) {
        this._msgBanner.alpha = this._msgTimer / 0.5;
      }
    }

    // Spawn message fade
    if (this._spawnMsgTimer > 0) {
      this._spawnMsgTimer -= dt;
      if (this._spawnMsgTimer < 0.8) {
        this._spawnMsg.alpha = this._spawnMsgTimer / 0.8;
        this._spawnMsg.y = 670 - (0.8 - this._spawnMsgTimer) * 30;
      } else {
        this._spawnMsg.y = 670;
      }
      if (this._spawnMsgTimer <= 0) {
        this._spawnMsg.text = '';
      }
    }
  }

  getTimeLeft() {
    return this._timer !== null ? this._time : Infinity;
  }

  isTimeUp() {
    return this._timer !== null && this._time <= 0;
  }

  destroy() {
    if (this.container.parent) this.container.parent.removeChild(this.container);
    this.container.destroy();
  }
}
